import * as Sentry from "@sentry/react";
import { isChunkReloadInFlight } from "./chunk-reload";
import {
  isSupabaseTransientNetworkError,
  isSupabaseJwtExpiredError,
} from "./sentry-filters";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (dsn) {
  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Reduced from 0.2 / 1.0 to curb org_rate_limited quota exhaustion.
    tracesSampleRate: 0.05,
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    // Cap error events at 50% — the beforeSend filters already drop the
    // noisiest categories, so this is a volume backstop, not signal loss.
    sampleRate: 0.5,
    enableLogs: true,
    // Drop (1) transient network failures on Supabase calls — the Supabase
    // Sentry integration reports every failed poll, even when app code
    // handles it (see sentry-filters.ts) — and (2) any error that fires
    // while a stale-chunk recovery reload is in flight (see chunk-reload.ts).
    //
    // Previously this only suppressed messages matching PRELOAD_ERROR_PATTERNS,
    // but React 19's lazyInitializer reads `.default` on the resolved module
    // object *before* loadRoute's guard runs, producing a TypeError that
    // escaped the pattern list (WEXARE-MVP-1R). Since the page is about to
    // reload anyway, every error during this window is transient noise.
    // A *failed* chunk recovery (second attempt) clears the in-flight flag
    // so its errors still flow through.
    beforeSend(event) {
      if (isSupabaseTransientNetworkError(event)) return null;
      if (isSupabaseJwtExpiredError(event)) return null;
      if (isChunkReloadInFlight()) return null;
      return event;
    },
  });
}

export const isSentryEnabled = !!dsn;
export { Sentry };
